<template>
  <b-container fluid>
    <admin-preloader :show="detailLoading"/>

    <b-row>
      <b-col sm="6">
        <admin-breadcrumb name="Universal Directory"/>
      </b-col>

      <b-col sm="6" class="text-right align-self-end">
        <b-button
            v-if="canCreate"
            variant="success"
            class="mb-4"
            @click="openCreate"
        >
          Add contact
        </b-button>
      </b-col>
    </b-row>

    <admin-panel with-footer>
      <b-form class="directory-filters" @submit.prevent="search">
        <div class="directory-filter-row">
          <b-input-group class="directory-search">
            <b-form-input
                id="directory-search"
                v-model="filters.search"
                aria-label="Search contacts and companies"
                placeholder="Name, company, DBA, email, phone or license"
                @input="scheduleSearch"
            />

            <b-input-group-append>
              <b-button
                  type="submit"
                  variant="primary"
                  :disabled="loading"
              >
                <b-spinner
                    v-if="loading"
                    small
                    class="mr-1"
                    aria-hidden="true"
                />

                {{ loading ? 'Searching…' : 'Search' }}
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <label for="directory-states" class="sr-only">
            States
          </label>

          <v-select
              v-model="filters.states"
              class="directory-states"
              input-id="directory-states"
              multiple
              :options="stateOptions"
              :reduce="option => option.value"
              placeholder="All States"
          />

          <b-form-select
              v-model="filters.account_status"
              class="directory-filter"
              :options="accountOptions"
              aria-label="Account status"
          />
        </div>

        <div class="directory-filter-row">
          <b-form-select
              v-model="filters.license_type_id"
              class="directory-filter"
              :options="licenseTypeOptions"
              :disabled="filterOptionsLoading || Boolean(filterOptionsError)"
              aria-label="License type"
          />

          <b-form-input
              v-model="filters.title"
              class="directory-filter"
              aria-label="Role or title"
              placeholder="Role / title"
          />

          <b-form-select
              v-model="filters.company_status_id"
              class="directory-filter"
              :options="companyStatusOptions"
              :disabled="filterOptionsLoading || Boolean(filterOptionsError)"
              aria-label="Company status"
          />

          <b-form-select
              v-model="filters.source"
              class="directory-filter"
              :options="sourceOptions"
              aria-label="Contact source"
          />

          <b-button type="submit" variant="outline-primary">
            Apply filters
          </b-button>

          <b-button variant="link" @click="reset">
            Clear
          </b-button>
        </div>
      </b-form>

      <b-alert
          :show="Boolean(filterOptionsError)"
          variant="warning"
          class="mt-3"
      >
        {{ filterOptionsError }}

        <b-button
            type="button"
            size="sm"
            variant="outline-secondary"
            :disabled="filterOptionsLoading"
            @click="loadFilterOptions"
        >
          Retry
        </b-button>
      </b-alert>

      <b-alert
          :show="Boolean(error)"
          variant="danger"
          class="mt-3"
      >
        {{ error }}

        <b-button size="sm" @click="load">
          Retry
        </b-button>
      </b-alert>

      <p class="text-muted my-2">
        {{ total }} contacts
        <span v-if="resultSearch">
          · Most relevant first
        </span>
      </p>

      <b-table
          :items="contacts"
          :fields="fields"
          primary-key="key"
          responsive
          show-empty
          :busy="loading"
          empty-text="No contacts match your search."
      >
        <template #table-busy>
          <div class="text-center py-3" role="status">
            <b-spinner small class="mr-2"/>
            Searching contacts…
          </div>
        </template>

        <template #cell(name)="{ item }">
          <b-button
              variant="link"
              class="p-0 text-left"
              @click="showContact(item)"
          >
            <admin-directory-highlight
                :text="contactName(item)"
                :search="resultSearch"
            />
          </b-button>

          <div class="text-muted">
            <admin-directory-highlight
                :text="item.title"
                :search="resultSearch"
            />
          </div>
        </template>

        <template #cell(contact)="{ item }">
          <div>
            <admin-directory-highlight
                :text="item.email || '—'"
                :search="resultSearch"
            />
          </div>

          <div>
            <admin-directory-highlight
                :text="item.phone || '—'"
                :search="resultSearch"
                normalize="phone"
            />
          </div>
        </template>

        <template #cell(company)="{ item }">
          <div
              v-for="(relationship, index) in item.relationships || []"
              :key="`${item.key}-relationship-${index}`"
              class="mb-2"
          >
            <strong>
              <admin-directory-highlight
                  :text="
                  relationship.company ||
                    relationship.facility ||
                    '—'
                "
                  :search="resultSearch"
              />
            </strong>

            <div
                v-if="
                relationship.company &&
                  relationship.facility &&
                  relationship.facility !== relationship.company
              "
            >
              <admin-directory-highlight
                  :text="relationship.facility"
                  :search="resultSearch"
              />
            </div>

            <div v-if="relationship.dba">
              DBA:
              <admin-directory-highlight
                  :text="relationship.dba"
                  :search="resultSearch"
              />
            </div>

            <div>
              {{
                [relationship.city, relationship.state]
                    .filter(Boolean)
                    .join(', ')
              }}
            </div>

            <small>
              <admin-directory-highlight
                  :text="relationship.license_number"
                  :search="resultSearch"
                  normalize="license"
              />

              <span
                  v-if="
                  relationship.license_number &&
                    relationship.license_type
                "
              >
                ·
              </span>

              {{ relationship.license_type }}
            </small>
          </div>
        </template>

        <template #cell(source)="{ item }">
          {{ sources[item.source] || item.source }}
        </template>
      </b-table>

      <b-pagination
          v-if="total > perPage"
          v-model="page"
          :total-rows="total"
          :per-page="perPage"
          @input="load"
      />
    </admin-panel>

    <b-modal
        v-model="detailVisible"
        title="Contact details"
        size="lg"
        ok-only
    >
      <template v-if="selected">
        <h5>{{ contactName(selected) }}</h5>

        <p>
          {{ selected.title || 'No title available' }}
        </p>

        <dl class="row">
          <dt class="col-sm-4">Email</dt>
          <dd class="col-sm-8">
            {{ selected.email || '—' }}
          </dd>

          <dt class="col-sm-4">Phone</dt>
          <dd class="col-sm-8">
            {{ selected.phone || '—' }}
          </dd>

          <dt class="col-sm-4">Greenlists account</dt>
          <dd class="col-sm-8">
            {{ selected.account_status }}

            <router-link
                v-if="selected.source === 'user'"
                :to="{
                name: 'admin-user',
                params: { id: selected.source_id }
              }"
            >
              Open user
            </router-link>
          </dd>

          <dt class="col-sm-4">Source</dt>
          <dd class="col-sm-8">
            {{ sources[selected.source] || selected.source }}
            #{{ selected.source_id }}
          </dd>
        </dl>

        <h6>Companies, facilities and licenses</h6>

        <p
            v-if="!selected.relationships || !selected.relationships.length"
            class="text-muted"
        >
          No linked company or license.
        </p>

        <b-card
            v-for="(relationship, index) in selected.relationships || []"
            :key="`selected-relationship-${index}`"
            class="mb-2"
        >
          <strong>
            {{
              relationship.company ||
              relationship.facility ||
              'Company unavailable'
            }}
          </strong>

          <router-link
              v-if="relationship.company_id && canReadCompanies"
              class="ml-2"
              :to="{
              name: 'admin-company',
              params: { id: relationship.company_id }
            }"
          >
            Open company
          </router-link>

          <div v-if="relationship.company_status">
            Company status: {{ relationship.company_status }}
          </div>

          <div v-if="relationship.dba">
            DBA: {{ relationship.dba }}
          </div>

          <div v-if="relationship.facility">
            Facility: {{ relationship.facility }}
          </div>

          <div>
            {{
              [
                relationship.license_number,
                relationship.license_type,
                relationship.license_status
              ]
                  .filter(Boolean)
                  .join(' · ')
            }}
          </div>

          <div>
            {{
              [
                relationship.address,
                relationship.city,
                relationship.state
              ]
                  .filter(Boolean)
                  .join(', ')
            }}
          </div>

          <router-link
              v-if="relationship.state_license_id"
              :to="{
              name: 'admin-state-license-detail',
              params: { id: relationship.state_license_id }
            }"
          >
            Open State License
          </router-link>
        </b-card>

        <b-button
            v-if="canCreate"
            variant="outline-primary"
            @click="openCreate(selected)"
        >
          Link company or license
        </b-button>

        <template
            v-if="
            selected.possible_duplicates &&
              selected.possible_duplicates.length
          "
        >
          <h6 class="mt-3">Possible matching contacts</h6>

          <p class="text-muted">
            These records share an email or phone. They have not been merged.
          </p>

          <div
              v-for="match in selected.possible_duplicates"
              :key="`${match.source}:${match.source_id}`"
          >
            <b-button
                variant="link"
                @click="showContact(match)"
            >
              {{ contactName(match) }} ·
              {{ sources[match.source] || match.source }}
            </b-button>
          </div>
        </template>
      </template>
    </b-modal>

    <b-modal
        v-model="createVisible"
        :title="
        form.existing_source
          ? 'Link existing contact'
          : 'Add directory contact'
      "
        :busy="saving"
        ok-title="Save"
        :ok-disabled="saving"
        @hidden="cancelLookups"
        @ok.prevent="save"
    >
      <b-alert
          :show="Boolean(formError)"
          variant="danger"
      >
        {{ formError }}
      </b-alert>

      <div
          v-if="duplicateCandidates.length"
          class="mb-3"
      >
        <p>
          Use an existing contact to retain its original record and add your
          selected relationships:
        </p>

        <b-button
            v-for="person in duplicateCandidates"
            :key="`${person.source}:${person.source_id}`"
            block
            variant="outline-primary"
            @click="reuseContact(person)"
        >
          {{ contactName(person) }} ·
          {{ sources[person.source] || person.source }}
        </b-button>

        <b-button
            class="mt-2"
            variant="outline-secondary"
            @click="keepSeparate"
        >
          Create a separate contact anyway
        </b-button>
      </div>

      <template v-if="!form.existing_source">
        <b-form-group
            v-for="field in contactFields"
            :key="field.key"
            :label="field.label"
            :label-for="`contact-${field.key}`"
            :invalid-feedback="validation[field.key]"
            :state="fieldState(field.key)"
        >
          <b-form-input
              :id="`contact-${field.key}`"
              v-model="form[field.key]"
              :type="field.key === 'email' ? 'email' : 'text'"
              :state="fieldState(field.key)"
          />
        </b-form-group>
      </template>

      <b-form-group
          label="Companies"
          label-for="directory-company-lookup"
      >
        <v-select
            v-model="chosenCompanies"
            input-id="directory-company-lookup"
            multiple
            label="name"
            :options="companyLookup.options"
            :filterable="false"
            :loading="companyLookup.loading"
            placeholder="Type to search companies"
            @search="value => scheduleLookup('company', value)"
        >
          <template #no-options>
            {{ lookupHint(companyLookup, 'companies') }}
          </template>
        </v-select>

        <b-form-invalid-feedback
            :state="!companyLookup.error"
        >
          {{ companyLookup.error }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group
          label="Facilities / State Licenses"
          label-for="directory-license-lookup"
      >
        <v-select
            v-model="chosenLicenses"
            input-id="directory-license-lookup"
            multiple
            label="label"
            :options="licenseLookup.options"
            :filterable="false"
            :loading="licenseLookup.loading"
            placeholder="Type a facility, DBA or license number"
            @search="value => scheduleLookup('license', value)"
        >
          <template #no-options>
            {{ lookupHint(licenseLookup, 'facilities or licenses') }}
          </template>
        </v-select>

        <b-form-invalid-feedback
            :state="!licenseLookup.error"
        >
          {{ licenseLookup.error }}
        </b-form-invalid-feedback>
      </b-form-group>

      <p class="text-muted">
        License information is read directly from the State License tool.
      </p>
    </b-modal>
  </b-container>
</template>

<script>
import vSelect from 'vue-select';

import AdminPreloader from '../components/AdminPreloader.vue';
import AdminBreadcrumb from '../components/AdminBreadcrumb.vue';
import AdminPanel from '../components/AdminPanel.vue';
import AdminDirectoryHighlight from '../components/AdminDirectoryHighlight.vue';
import {API_ROUTES} from '../api';

const SEARCH_DELAY = 350;
const LOOKUP_DELAY = 350;

const DIRECTORY_SOURCES = {
  user: 'Greenlists user',
  contact: 'Company / customer contact',
  facility_contact: 'Facility contact',
  license: 'Facility email / phone',
  vendor: 'External vendor contact',
  sales_rep: 'Sales representative',
  admin: 'Admin-created contact'
};

const DIRECTORY_FIELDS = [
  {
    key: 'name',
    label: 'Contact'
  },
  {
    key: 'contact',
    label: 'Email / phone'
  },
  {
    key: 'company',
    label: 'Company / facility / license'
  },
  {
    key: 'account_status',
    label: 'Account status'
  },
  {
    key: 'source',
    label: 'Source'
  }
];

const CONTACT_FIELDS = [
  {
    key: 'first_name',
    label: 'First name'
  },
  {
    key: 'last_name',
    label: 'Last name'
  },
  {
    key: 'title',
    label: 'Title / role'
  },
  {
    key: 'email',
    label: 'Email'
  },
  {
    key: 'phone',
    label: 'Phone'
  }
];

function createEmptyFilters() {
  return {
    search: '',
    states: [],
    license_type_id: null,
    title: '',
    account_status: '',
    company_status_id: null,
    source: ''
  };
}

function createEmptyLookup() {
  return {
    search: '',
    timer: null,
    requestId: 0,
    loading: false,
    error: '',
    options: []
  };
}

function extractErrorMessage(error, fallback) {
  const message =
      error &&
      error.response &&
      error.response.data &&
      error.response.data.message;

  return message || fallback;
}

export default {
  name: 'AdminUniversalDirectory',

  components: {
    vSelect,
    AdminPreloader,
    AdminBreadcrumb,
    AdminPanel,
    AdminDirectoryHighlight
  },

  data() {
    return {
      contacts: [],
      total: 0,
      page: 1,
      perPage: 25,

      loading: false,
      detailLoading: false,
      saving: false,

      error: '',
      formError: '',

      searchTimer: null,
      requestId: 0,
      detailRequestId: 0,

      resultSearch: '',
      states: {},
      sources: DIRECTORY_SOURCES,
      fields: DIRECTORY_FIELDS,
      contactFields: CONTACT_FIELDS,

      filters: createEmptyFilters(),
      appliedFilters: createEmptyFilters(),

      selected: null,
      detailVisible: false,

      form: {},
      validation: {},
      createVisible: false,
      duplicateCandidates: [],

      companyLookup: createEmptyLookup(),
      licenseLookup: createEmptyLookup(),

      chosenCompanies: [],
      chosenLicenses: [],

      licenseTypes: [],
      companyStatuses: [],
      filterOptionsLoading: false,
      filterOptionsError: '',
      filterOptionsRequestId: 0,
    };
  },

  computed: {
    licenseTypeOptions() {
      return [
        {
          value: null,
          text: 'All license types'
        },
        ...this.licenseTypes.map(type => ({
          value: Number(type.id),
          text: type.name
        }))
      ];
    },

    companyStatusOptions() {
      return [
        {
          value: null,
          text: 'All company statuses'
        },
        ...this.companyStatuses.map(status => ({
          value: Number(status.id),
          text: status.name
        }))
      ];
    },

    canCreate() {
      return this.$store.getters[
          'auth/hasAdminPermissions'
          ]('user_create');
    },

    canReadCompanies() {
      return this.$store.getters[
          'auth/hasAdminPermissions'
          ]('company_read');
    },

    stateOptions() {
      return Object.entries(this.states).map(([value, label]) => ({
        value,
        label
      }));
    },

    sourceOptions() {
      return [
        {
          value: '',
          text: 'All sources'
        },
        ...Object.entries(this.sources).map(([value, text]) => ({
          value,
          text
        }))
      ];
    },

    accountOptions() {
      return [
        {
          value: '',
          text: 'All statuses'
        },
        'Active',
        'Inactive',
        'Invited',
        'No linked account'
      ];
    }
  },

  methods: {
    contactName(person) {
      return (
          [
            person.first_name,
            person.last_name
          ]
              .filter(Boolean)
              .join(' ') ||
          person.email ||
          person.phone ||
          'Company contact'
      );
    },

    fieldState(field) {
      return this.validation[field] ? false : null;
    },

    errorMessage(error, fallback) {
      return extractErrorMessage(error, fallback);
    },

    scheduleSearch() {
      clearTimeout(this.searchTimer);

      /*
       * Invalidate the current request immediately. This prevents results
       * for older search text from replacing newer results.
       */
      this.requestId += 1;
      this.loading = false;

      this.searchTimer = setTimeout(() => {
        this.search();
      }, SEARCH_DELAY);
    },

    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;

      this.appliedFilters = {
        ...this.filters,
        states: [...this.filters.states]
      };

      this.page = 1;
      this.load();
    },

    reset() {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;

      this.filters = createEmptyFilters();
      this.search();
    },

    async load() {
      const requestId = ++this.requestId;

      this.loading = true;
      this.error = '';

      try {
        const {data} = await this.$api.get(
            API_ROUTES.ADMIN_UNIVERSAL_DIRECTORY,
            {
              params: {
                ...this.appliedFilters,
                page: this.page,
                per_page: this.perPage
              }
            }
        );

        if (requestId !== this.requestId) {
          return;
        }

        const result = data.result || {};

        this.contacts = result.data || [];
        this.total = result.total || 0;
        this.resultSearch = this.appliedFilters.search.trim();
      } catch (error) {
        if (requestId !== this.requestId) {
          return;
        }

        this.error = this.errorMessage(
            error,
            'Failed to load directory.'
        );

        this.contacts = [];
        this.total = 0;
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },

    async showContact(person) {
      const requestId = ++this.detailRequestId;

      this.detailLoading = true;

      try {
        const {data} = await this.$api.get(
            `${API_ROUTES.ADMIN_UNIVERSAL_DIRECTORY}/${person.source}/${person.source_id}`
        );

        if (requestId !== this.detailRequestId) {
          return;
        }

        this.selected = {
          ...data.contact,
          relationships: data.contact.relationships || [],
          possible_duplicates:
              data.contact.possible_duplicates || []
        };

        this.detailVisible = true;
      } catch (error) {
        if (requestId !== this.detailRequestId) {
          return;
        }

        this.$bvToast.toast(
            this.errorMessage(error, 'Failed to load contact.'),
            {
              title: 'Error',
              variant: 'danger'
            }
        );
      } finally {
        if (requestId === this.detailRequestId) {
          this.detailLoading = false;
        }
      }
    },

    openCreate(person = null) {
      this.form = person
          ? {
            existing_source: person.source,
            existing_id: person.source_id
          }
          : {};

      this.validation = {};
      this.formError = '';
      this.duplicateCandidates = [];

      this.cancelLookups();

      this.companyLookup = createEmptyLookup();
      this.licenseLookup = createEmptyLookup();

      this.chosenCompanies = [];
      this.chosenLicenses = [];

      this.detailVisible = false;
      this.createVisible = true;
    },

    cancelLookups() {
      [
        this.companyLookup,
        this.licenseLookup
      ].forEach(lookup => {
        clearTimeout(lookup.timer);

        lookup.timer = null;
        lookup.requestId += 1;
        lookup.loading = false;
      });
    },

    lookupHint(lookup, label) {
      if (lookup.search.length < 2) {
        return 'Type at least two characters to search.';
      }

      if (lookup.timer) {
        return 'Waiting for typing to finish…';
      }

      if (lookup.loading) {
        return 'Searching…';
      }

      return lookup.error || `No matching ${label}.`;
    },

    scheduleLookup(kind, value = '') {
      const lookup =
          kind === 'company'
              ? this.companyLookup
              : this.licenseLookup;

      clearTimeout(lookup.timer);

      lookup.timer = null;
      lookup.requestId += 1;
      lookup.search = value.trim();
      lookup.error = '';
      lookup.options = [];
      lookup.loading = false;

      if (
          !this.createVisible ||
          lookup.search.length < 2
      ) {
        return;
      }

      const requestId = lookup.requestId;

      lookup.timer = setTimeout(() => {
        if (
            requestId !== lookup.requestId ||
            !this.createVisible
        ) {
          return;
        }

        lookup.timer = null;
        this.loadLookup(kind, lookup, requestId);
      }, LOOKUP_DELAY);
    },

    async loadLookup(kind, lookup, requestId) {
      lookup.loading = true;

      try {
        const {data} = await this.$api.get(
            `${API_ROUTES.ADMIN_UNIVERSAL_DIRECTORY}/options`,
            {
              params: {
                search: lookup.search
              }
            }
        );

        if (requestId !== lookup.requestId) {
          return;
        }

        if (kind === 'company') {
          lookup.options = data.companies || [];
          return;
        }

        lookup.options = (data.licenses || []).map(license => ({
          ...license,
          label: [
            license.facility_name,
            license.license_number,
            license.state
          ]
              .filter(Boolean)
              .join(' · ')
        }));
      } catch (error) {
        if (requestId === lookup.requestId) {
          lookup.error = this.errorMessage(
              error,
              'Search failed. Please try again.'
          );
        }
      } finally {
        if (requestId === lookup.requestId) {
          lookup.loading = false;
        }
      }
    },

    reuseContact(person) {
      this.form = {
        existing_source: person.source,
        existing_id: person.source_id
      };

      this.duplicateCandidates = [];
      this.save();
    },

    keepSeparate() {
      this.$set(this.form, 'allow_duplicate', true);

      this.duplicateCandidates = [];
      this.save();
    },

    async save() {
      if (this.saving) {
        return;
      }

      this.saving = true;
      this.validation = {};
      this.formError = '';

      try {
        const payload = {
          ...this.form,
          company_ids: this.chosenCompanies.map(company => company.id),
          state_license_ids: this.chosenLicenses.map(
              license => license.id
          )
        };

        const {data} = await this.$api.post(
            API_ROUTES.ADMIN_UNIVERSAL_DIRECTORY,
            payload
        );

        this.createVisible = false;

        await this.load();

        if (data.contact) {
          await this.showContact(data.contact);
        }
      } catch (error) {
        const responseData =
            error &&
            error.response &&
            error.response.data;

        this.formError = this.errorMessage(
            error,
            'Failed to save contact.'
        );

        this.duplicateCandidates =
            (responseData && responseData.possible_duplicates) || [];

        const errors =
            (responseData && responseData.errors) || {};

        this.validation = Object.fromEntries(
            Object.entries(errors).map(([field, messages]) => [
              field,
              Array.isArray(messages)
                  ? messages.join(' ')
                  : messages
            ])
        );
      } finally {
        this.saving = false;
      }
    },

    async loadFilterOptions() {
      const requestId = ++this.filterOptionsRequestId;

      this.filterOptionsLoading = true;
      this.filterOptionsError = '';

      try {
        const { data } = await this.$api.get(
            `${API_ROUTES.ADMIN_UNIVERSAL_DIRECTORY}/filters`
        );

        if (requestId !== this.filterOptionsRequestId) {
          return;
        }

        this.states = data.states || {};
        this.licenseTypes = data.license_types || [];
        this.companyStatuses = data.company_statuses || [];
      } catch (error) {
        if (requestId !== this.filterOptionsRequestId) {
          return;
        }

        this.filterOptionsError = this.errorMessage(
            error,
            'Failed to load filter options. Please try again.'
        );
      } finally {
        if (requestId === this.filterOptionsRequestId) {
          this.filterOptionsLoading = false;
        }
      }
    },
  },

  created() {
    this.load();
    this.loadFilterOptions();
  },

  beforeDestroy() {
    this.cancelLookups();
    clearTimeout(this.searchTimer);

    this.requestId += 1;
    this.detailRequestId += 1;
    this.filterOptionsRequestId += 1;
  },
};
</script>

<style scoped>
.directory-filters {
  margin: 12px 0 8px;
}

.directory-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.directory-filter {
  flex: 1 1 160px;
  width: auto;
  min-width: 140px;
}

.directory-search {
  flex: 2 1 320px;
  width: auto;
}

.directory-states {
  flex: 1 1 190px;
  min-width: 190px;
}

.directory-filters .form-control,
.directory-filters .custom-select,
.directory-filters .btn {
  min-height: 36px;
  height: 36px;
  padding-top: 6px;
  padding-bottom: 6px;
  font-size: 14px;
}

.directory-states ::v-deep .vs__dropdown-toggle {
  min-height: 36px;
  padding: 0 0 3px;
  border-color: #e9ecef;
  border-radius: 0;
}

.directory-states ::v-deep .vs__search,
.directory-states ::v-deep .vs__selected {
  font-size: 14px;
}

@media (max-width: 575px) {
  .directory-search,
  .directory-states {
    flex-basis: 100%;
  }
}
</style>